import { useCallback, useEffect, useState } from "react";
import { usePagedList } from "./usePagedList";

/** The search is sent a moment after typing stops, not on every keystroke. */
const DEFAULT_SEARCH_DELAY_MS = 400;

/**
 * A server-paged list with a search box whose text the SERVER matches (#184 reopened): the Bid
 * History pages used to filter only the rows of the page they had loaded, so anything on another
 * page was never found. This is the pattern Auction Results (#147) and the Managers page (#133)
 * write out by hand: the typed text is debounced, trimmed, and sent as SearchTerm, and a new term
 * makes usePagedList start again from page 1 with one request.
 *
 * @param {(query: {PageNumber: number, PageSize: number, SortOrder: boolean, SearchTerm: string}) => Promise<any>} fetcher
 *   An auth-service wrapper that sends SearchTerm when it is not empty. Pass the imported function
 *   itself: it is an effect dependency, so a new function on every render would refetch each time.
 * @param {number} [delayMs] How long after the last keystroke the search is sent.
 * @returns {object} usePagedList's values, plus searchText (the box's text), setSearchText, and
 *   clearSearch, which empties the box and asks for the unfiltered list at once.
 */
export const useSearchedPagedList = (fetcher, delayMs = DEFAULT_SEARCH_DELAY_MS) => {
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchText.trim()), delayMs);
    return () => clearTimeout(timer);
  }, [searchText, delayMs]);

  // A new fetchPage is a new filter, which usePagedList answers from page 1.
  const fetchPage = useCallback((query) => fetcher({ ...query, SearchTerm: search }), [fetcher, search]);
  const pager = usePagedList(fetchPage);

  const clearSearch = () => {
    setSearchText("");
    setSearch("");
  };

  return { ...pager, searchText, setSearchText, clearSearch };
};
